import { PillProps } from 'ext-ui/components/Pill/Pill'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { warning_text } from 'ext-ui/style/colors'
import { Moment } from 'moment-timezone'
import React from 'react'
import EmptyLabel from 'src/components/EmptyLabel/EmptyLabel'
import SvgAutoReview from 'src/components/Svgs/SvgAutoReview'
import SvgCheckNew from 'src/components/Svgs/SvgCheckNew'
import SvgClock from 'src/components/Svgs/SvgClock'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { defaultCurrency } from 'src/configurations'
import { ExtendCurrency, OrganizationConnectedPlatform } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Dte, { dateFormatMoment } from 'src/localization/Dte'
import { translateNode } from 'src/localization/translateNode'
import {
  ReceiptAttachmentModel,
  ReceiptAttachmentResponse,
} from 'src/models/receiptAttachment.models'
import {
  ReceiptMatchingStatuses,
  ReceiptVerificationStatus,
} from 'src/models/receiptVerification.models'
import { AdditionalFieldsTransactionResponse } from 'src/queries/organizations/additionalFields.queries'
import {
  ExpenseCategoryResponse,
  ExpenseCategoryType,
} from 'src/queries/organizations/expenseCategories.queries'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

export enum TransactionType {
  Debit = 'DEBIT',
  Credit = 'CREDIT',
  Avs = 'AVS',
}

export enum TransactionStatus {
  Pending = 'PENDING',
  Cleared = 'CLEARED',
  Declined = 'DECLINED',
  NoMatch = 'NO_MATCH',
  AvsPass = 'AVS_PASS',
  AvsFail = 'AVS_FAIL',
  AuthReversal = 'AUTH_REVERSAL',
}

export enum TransactionSource {
  Physical = 'PHYSICAL',
  Virtual = 'VIRTUAL',
  Other = 'OTHER',
}

export enum TransactionUserType {
  CardUser = 'CardUser',
  CardCreator = 'CardCreator',
}

export enum TransactionExpenseCategoryStatus {
  Attached = 'Attached',
  Missing = 'Missing',
}

export enum TransactionReceiptStatus {
  Attached = 'Attached',
  Missing = 'Missing',
}

export enum TransactionReceiptRecoveryStatus {
  NeedsReview = 'NEEDS_REVIEW',
  WaitingOnSpender = 'WAITING_ON_SPENDER',
  ReceiptReconciled = 'RECEIPT_RECONCILED',
}

export enum TransactionDateType {
  Transaction = 'Transaction',
  Clear = 'Clear',
}

export enum ConnectedPlatformStatus {
  NotSynced = 'NOT_SYNCED',
  Pending = 'PENDING',
  Success = 'SUCCESS',
  Failed = 'FAILED',
}

export enum TransactionFinalApprovalStatus {
  Flagged = 'FLAGGED',
  PendingReview = 'PENDING_REVIEW',
  Reviewed = 'REVIEWED',
}

export enum TransactionManagerReviewStatus {
  AutoReviewed = 'AUTO_REVIEWED',
  Reviewed = 'REVIEWED',
  Rejected = 'REJECTED',
  PendingReview = 'PENDING_REVIEW',
}

export enum TransactionManagerRejectionReason {
  MissingReceipt = 'MISSING_RECEIPT',
  IncorrectReceipt = 'INCORRECT_RECEIPT',
  MissingExpenseCategories = 'MISSING_EXPENSE_CATEGORIES',
  IncorrectExpenseCategories = 'INCORRECT_EXPENSE_CATEGORIES',
  MissingNote = 'MISSING_NOTE',
  OutOfPolicy = 'OUT_OF_POLICY',
  Other = 'OTHER',
}

export enum TransactionSort {
  dateClearDesc = '-dateClearedAt',
  dateClearAsc = 'dateClearedAt',
  dateAuthDesc = '-dateAuthedAt',
  dateAuthAsc = 'dateAuthedAt',
  merchantNameDesc = '-merchantName',
  merchantNameAsc = 'merchantName',
  amountDesc = '-amount',
  amountAsc = 'amount',
  recipientNameDesc = '-recipientName',
  recipientNameAsc = 'recipientName',
  tasksRejectedFirst = '-dateClearedAtRejectedFirst',
}

export function transactionManagerReviewStatusToString(
  status?: TransactionManagerReviewStatus,
): string {
  switch (status) {
    case TransactionManagerReviewStatus.AutoReviewed:
      return 'Auto-reviewed'
    case TransactionManagerReviewStatus.Reviewed:
      return 'Reviewed'
    case TransactionManagerReviewStatus.Rejected:
      return 'Rejected'
    case TransactionManagerReviewStatus.PendingReview:
    default:
      return 'Pending Review'
  }
}

export function transactionManagerReviewStatusToColor(
  status?: TransactionManagerReviewStatus,
): PillProps['color'] {
  switch (status) {
    case TransactionManagerReviewStatus.AutoReviewed:
      return 'new'
    case TransactionManagerReviewStatus.Reviewed:
      return 'highlight'
    case TransactionManagerReviewStatus.Rejected:
      return 'severe'
    case TransactionManagerReviewStatus.PendingReview:
    default:
      return 'pending'
  }
}

export function transactionManagerReviewStatusToIcon(
  status?: TransactionManagerReviewStatus,
): React.ReactElement {
  switch (status) {
    case TransactionManagerReviewStatus.AutoReviewed:
      return <SvgAutoReview height={16} width={16} />
    case TransactionManagerReviewStatus.Reviewed:
      return <SvgCheckNew height={14} width={14} />
    case TransactionManagerReviewStatus.Rejected:
      return <SvgFlagNew height={14} width={14} />
    case TransactionManagerReviewStatus.PendingReview:
    default:
      return <SvgClock stroke={warning_text} height={14} width={14} />
  }
}

export function transactionManagerReviewRejectionReasonToString(
  status?: TransactionManagerRejectionReason,
): string {
  switch (status) {
    case TransactionManagerRejectionReason.MissingReceipt:
      return 'Receipt Missing'
    case TransactionManagerRejectionReason.IncorrectReceipt:
      return 'Receipt Incorrect'
    case TransactionManagerRejectionReason.MissingExpenseCategories:
      return 'Expense Category Missing'
    case TransactionManagerRejectionReason.IncorrectExpenseCategories:
      return 'Expense Category Incorrect'
    case TransactionManagerRejectionReason.MissingNote:
      return 'Note/Justification Missing'
    case TransactionManagerRejectionReason.OutOfPolicy:
      return 'Not a business expense'
    case TransactionManagerRejectionReason.Other:
    default:
      return 'Other'
  }
}

export function transactionReviewStatusToString(
  status: TransactionFinalApprovalStatus,
): string {
  switch (status) {
    case TransactionFinalApprovalStatus.Flagged:
      return 'Flagged'
    case TransactionFinalApprovalStatus.PendingReview:
      return 'Pending Approval'
    case TransactionFinalApprovalStatus.Reviewed:
      return 'Approved'
  }
}

export function transactionReviewStatusToColor(
  status: TransactionFinalApprovalStatus,
): PillProps['color'] {
  switch (status) {
    case TransactionFinalApprovalStatus.Flagged:
      return 'severe'
    case TransactionFinalApprovalStatus.PendingReview:
      return 'pending'
    case TransactionFinalApprovalStatus.Reviewed:
      return 'highlight'
  }
}

export interface TransactionConnectedPlatformListResponse {
  id: string
  transactionId: string
  platform: OrganizationConnectedPlatform
  syncStatus: ConnectedPlatformStatus
}

export class TransactionConnectedPlatformModel {
  public id: string
  public transactionId: string
  public platform: OrganizationConnectedPlatform
  public syncStatus: ConnectedPlatformStatus

  public constructor(data: TransactionConnectedPlatformListResponse) {
    this.id = data.id
    this.transactionId = data.transactionId
    this.platform = data.platform
    this.syncStatus = data.syncStatus
  }
}

export interface IdNameResponse {
  id: string
  name: string
}

export class IdNameModel {
  public id: string
  public name: string

  public constructor(data: IdNameResponse) {
    this.id = data.id
    this.name = data.name
  }
}

export interface SupplierResponse extends IdNameResponse {
  requiresConfirmation?: boolean
}

export class SupplierModel extends IdNameModel {
  public requiresConfirmation: boolean
  public constructor(data: SupplierResponse) {
    super(data)
    this.requiresConfirmation = data.requiresConfirmation ?? false
  }
}

export interface ExpenseDetailResponse {
  categoryId: string
  categoryName: string
  categoryType: ExpenseCategoryType
  categoryArchivedAt?: string
  labelId?: string
  labelName: string
  subLabelName?: string
  labelArchivedAt?: string
  ruleId?: string
  errorMessage?: string
  generatedByPrediction: boolean
  transactionSplitId?: string | null
  splitIndex?: number | null
  splitAmountCents?: number | null
}

export class ExpenseDetailsModel {
  public categoryId: string
  public categoryName: string
  public categoryType: ExpenseCategoryType
  public categoryArchivedAt?: string
  public labelId?: string
  public labelName: string
  public subLabelName?: string
  public labelArchivedAt?: string
  public ruleId?: string
  public errorMessage?: string
  public generatedByPrediction: boolean
  public transactionSplitId?: string | null
  public splitIndex?: number | null
  public splitAmountCents?: number | null

  public constructor(data: ExpenseDetailResponse) {
    this.categoryId = data.categoryId
    this.categoryName = data.categoryName
    this.categoryType = data.categoryType
    this.categoryArchivedAt = data.categoryArchivedAt
    this.labelId = data.labelId
    this.labelName = data.labelName
    this.subLabelName = data.subLabelName
    this.labelArchivedAt = data.labelArchivedAt
    this.ruleId = data.ruleId
    this.errorMessage = data.errorMessage
    this.generatedByPrediction = data.generatedByPrediction
    this.transactionSplitId = data.transactionSplitId ?? null
    this.splitIndex = data.splitIndex ?? null
    this.splitAmountCents = data.splitAmountCents ?? null
  }
}

export interface NormalizedMerchant {
  normalizedMerchantName: string
  normalizedMerchantImageUrls?: {
    small?: string
    medium?: string
    original?: string
  }
}

export enum TransactionAiInsightType {
  PolicyAgent = 'POLICY_AGENT',
}

export enum TransactionAiInsightFeedbackType {
  ThumbsUp = 'THUMBS_UP',
  ThumbsDown = 'THUMBS_DOWN',
  None = 'NONE',
}

export interface TransactionAiInsights {
  id?: string
  insightType: TransactionAiInsightType
  insightText?: string
  keyPoints?: string
  insightJson?: Record<string, string>
  feedbackType?: TransactionAiInsightFeedbackType | null
}

export type MccGroupKey =
  | 'AIRLINE'
  | 'ENTERTAINMENT'
  | 'ASSOCIATIONS'
  | 'AUTOMOBILES'
  | 'BUSINESS'
  | 'CAR_RENTAL'
  | 'EDUCATION'
  | 'FINANCIAL'
  | 'GAS'
  | 'GOVERNMENT'
  | 'LODGING'
  | 'MAIL_PHONE_ORDER'
  | 'PERSONAL'
  | 'PROFESSIONAL'
  | 'REPAIR'
  | 'RESTAURANTS'
  | 'RETAIL'
  | 'TELECOM'
  | 'TRANSPORTATION'
  | 'UTILITIES'
  | 'WHOLESALE'
  | 'OTHER'

export interface TransactionListResponse {
  id: string
  cardholderName: string
  cardholderEmail: string
  recipientName: string
  recipientEmail: string
  recipientId: string
  teamId: string
  receiptRequired: boolean
  organizationReceiptRequired: boolean
  nameOnCard?: string
  source: TransactionSource
  vcnLast4: string
  vcnDisplayName: string
  virtualCardId?: string
  type: TransactionType
  managerReviewStatus?: TransactionManagerReviewStatus
  managerRejectionReason?: TransactionManagerRejectionReason
  managerRejectionNote?: string
  managerReviewId?: string
  managerId?: string
  status: string
  declineReason?: string
  approvalCode?: string
  authBillingAmountCents?: number
  authBillingCurrency?: ExtendCurrency
  authMerchantAmountCents?: number
  authMerchantCurrency?: ExtendCurrency
  authExchangeRate?: number
  clearingBillingAmountCents?: number
  clearingBillingCurrency?: ExtendCurrency
  clearingMerchantAmountCents?: number
  clearingMerchantCurrency?: ExtendCurrency
  clearingExchangeRate?: number
  mcc?: string
  mccGroup?: string
  mccGroupKey?: MccGroupKey
  mccDescription?: string
  merchantId?: string
  merchantName?: string
  merchantAddress?: string
  merchantCity?: string
  merchantState?: string
  merchantCountry?: string
  merchantZip?: string
  authedAt?: string
  clearedAt?: string
  updatedAt: string
  hasAttachments: boolean
  isSplit?: boolean
  referenceId?: string
  expenseDetails?: ExpenseDetailsModel[]
  additionalFields?: AdditionalFieldsTransactionResponse[]
  creditCardId: string
  attachmentsCount?: number
  attachments?: ReceiptAttachmentResponse[]
  receiptThumbnailUrl?: string
  receiptMatchingStatus?: ReceiptVerificationStatus
  receiptMatchingStatuses?: ReceiptMatchingStatuses
  creditCardDisplayName: string
  creditCardType: string
  parentCreditCardId?: string
  parentCreditCardDisplayName?: string
  connectedPlatforms?: TransactionConnectedPlatformListResponse[]
  reviewStatus?: TransactionFinalApprovalStatus
  normalizedMerchant?: NormalizedMerchant
  receiptAttachmentSyncStatus?: ConnectedPlatformStatus
  receiptAgentStatus?: ReceiptRecoveryLifecycleState
  receiptAgentAction?: string
  transactionAiInsights?: TransactionAiInsights[]
}

export class TransactionListModel {
  public id: string
  public cardholderName: string
  public cardholderEmail: string
  public recipientName: string
  public recipientEmail: string
  public recipientId: string
  public teamId: string
  public receiptRequired: boolean
  public organizationReceiptRequired: boolean
  public nameOnCard?: string
  public source: TransactionSource
  public vcnLast4: string
  public vcnDisplayName: string
  public virtualCardId?: string
  public type: TransactionType
  public managerId?: string
  public managerReviewStatus?: TransactionManagerReviewStatus
  public managerRejectionReason?: TransactionManagerRejectionReason
  public managerRejectionNote?: string
  public managerReviewId?: string
  public status: string
  public declineReason?: string
  public approvalCode?: string
  public authBillingAmountCents?: number
  public authBillingCurrency?: ExtendCurrency
  public authMerchantAmountCents?: number
  public authMerchantCurrency?: ExtendCurrency
  public authExchangeRate?: number
  public clearingBillingAmountCents?: number
  public clearingBillingCurrency?: ExtendCurrency
  public clearingMerchantAmountCents?: number
  public clearingMerchantCurrency?: ExtendCurrency
  public clearingExchangeRate?: number
  public mcc?: string
  public mccGroup?: string
  public mccGroupKey?: MccGroupKey
  public mccDescription?: string
  public merchantId?: string
  public merchantName?: string
  public merchantAddress?: string
  public merchantCity?: string
  public merchantState?: string
  public merchantCountry?: string
  public merchantZip?: string
  public authedAt?: string
  public clearedAt?: string
  public updatedAt: string
  public hasAttachments: boolean
  public isSplit: boolean
  public expenseDetails?: ExpenseDetailsModel[]
  public additionalFields?: AdditionalFieldsTransactionResponse[]
  public referenceId?: string
  public creditCardId: string
  public parentCreditCardId?: string
  public parentCreditCardDisplayName?: string
  public attachmentsCount?: number
  public attachments?: ReceiptAttachmentModel[]
  public receiptThumbnailUrl?: string
  public receiptMatchingStatus?: ReceiptVerificationStatus
  public receiptMatchingStatuses?: ReceiptMatchingStatuses
  public creditCardDisplayName: string
  public creditCardType: string
  public connectedPlatforms?: TransactionConnectedPlatformModel[]
  public reviewStatus: TransactionFinalApprovalStatus
  public normalizedMerchant?: NormalizedMerchant
  public receiptAttachmentSyncStatus?: ConnectedPlatformStatus
  public receiptAgentStatus?: ReceiptRecoveryLifecycleState
  public receiptAgentAction?: string
  public transactionAiInsights?: TransactionAiInsights[]

  public constructor(data: TransactionListResponse) {
    this.id = data.id
    this.cardholderName = data.cardholderName
    this.cardholderEmail = data.cardholderEmail
    this.recipientName = data.recipientName
    this.recipientEmail = data.recipientEmail
    this.recipientId = data.recipientId
    this.teamId = data.teamId
    this.receiptRequired = data.receiptRequired
    this.organizationReceiptRequired = data.organizationReceiptRequired
    this.nameOnCard = data.nameOnCard
    this.source = data.source
    this.vcnLast4 = data.vcnLast4
    this.vcnDisplayName = data.vcnDisplayName
    this.virtualCardId = data.virtualCardId
    this.type = data.type
    this.managerId = data.managerId
    this.managerReviewStatus = data.managerReviewStatus
    this.managerRejectionReason = data.managerRejectionReason
    this.managerRejectionNote = data.managerRejectionNote
    this.managerReviewId = data.managerReviewId
    this.status = data.status
    this.declineReason = data.declineReason
    this.approvalCode = data.approvalCode
    this.authBillingAmountCents = data.authBillingAmountCents
    this.authBillingCurrency = data.authBillingCurrency
    this.authMerchantAmountCents = data.authMerchantAmountCents
    this.authMerchantCurrency = data.authMerchantCurrency
    this.authExchangeRate = data.authExchangeRate
    this.clearingBillingAmountCents = data.clearingBillingAmountCents
    this.clearingBillingCurrency = data.clearingBillingCurrency
    this.clearingMerchantAmountCents = data.clearingMerchantAmountCents
    this.clearingMerchantCurrency = data.clearingMerchantCurrency
    this.clearingExchangeRate = data.clearingExchangeRate
    this.mcc = data.mcc
    this.mccGroup = data.mccGroup
    this.mccGroupKey = data.mccGroupKey
    this.mccDescription = data.mccDescription
    this.merchantId = data.merchantId
    this.merchantName = data.merchantName
    this.merchantAddress = data.merchantAddress
    this.merchantCity = data.merchantCity
    this.merchantState = data.merchantState
    this.merchantCountry = data.merchantCountry
    this.merchantZip = data.merchantZip
    this.authedAt = data.authedAt
    this.clearedAt = data.clearedAt
    this.updatedAt = data.updatedAt
    this.hasAttachments = data.hasAttachments
    this.isSplit = data.isSplit ?? false
    this.referenceId = data.referenceId
    this.expenseDetails =
      data.expenseDetails !== undefined
        ? data.expenseDetails.map(
            (expDetail) => new ExpenseDetailsModel(expDetail),
          )
        : undefined
    this.additionalFields = data.additionalFields
    this.creditCardId = data.creditCardId
    this.parentCreditCardId = data.parentCreditCardId
    this.parentCreditCardDisplayName = data.parentCreditCardDisplayName
    this.attachmentsCount = data.attachmentsCount
    this.attachments =
      data.attachments !== undefined
        ? data.attachments.map((a) => new ReceiptAttachmentModel(a))
        : undefined
    this.receiptThumbnailUrl = data.receiptThumbnailUrl
    this.receiptMatchingStatus = data.receiptMatchingStatus
    this.receiptMatchingStatuses = data.receiptMatchingStatuses
    this.creditCardDisplayName = data.creditCardDisplayName
    this.creditCardType = data.creditCardType
    this.connectedPlatforms =
      data.connectedPlatforms !== undefined
        ? data.connectedPlatforms.map(
            (cp) => new TransactionConnectedPlatformModel(cp),
          )
        : undefined
    this.reviewStatus =
      data.reviewStatus ?? TransactionFinalApprovalStatus.PendingReview
    this.normalizedMerchant = data.normalizedMerchant
    this.receiptAttachmentSyncStatus = data.receiptAttachmentSyncStatus
    this.receiptAgentStatus = data.receiptAgentStatus
    this.receiptAgentAction = data.receiptAgentAction
    this.transactionAiInsights = data.transactionAiInsights
  }

  public get merchantNameOrUnknown(): string | React.ReactElement {
    return this.merchantName !== undefined
      ? this.merchantName
      : this.isPending
        ? translateNode('Merchant Pending')
        : translateNode('Unknown Merchant')
  }

  public get normalizedMerchantNameOrUnknown(): string | React.ReactElement {
    return (
      this.normalizedMerchant?.normalizedMerchantName ??
      (this.merchantName !== undefined
        ? this.merchantName
        : this.isPending
          ? translateNode('Merchant Pending')
          : translateNode('Unknown Merchant'))
    )
  }

  public merchantImageSrc(
    size: 'small' | 'medium' | 'original',
  ): string | undefined {
    return this.normalizedMerchant?.normalizedMerchantImageUrls?.[size]
  }

  public get mccGroupIfKnownMerchant(): string | React.ReactElement {
    return this.mccGroup !== undefined && this.merchantName !== undefined ? (
      this.mccGroup
    ) : (
      <EmptyLabel />
    )
  }

  public get date(): string | undefined {
    return this.authedAt !== undefined
      ? this.authedAt
      : this.clearedAt !== undefined
        ? this.clearedAt
        : undefined
  }

  public get transactionDate(): React.ReactElement | undefined {
    const transactionDate = this.date
    return transactionDate ? (
      translateNode('${date} at ${timeLabel}', {
        date: <Dte date={transactionDate} format={'l'} />,
        timeLabel: <Dte date={transactionDate} format={'LT'} />,
      })
    ) : (
      <EmptyLabel />
    )
  }

  public get dateString(): React.ReactElement | undefined {
    return this.date !== undefined ? (
      <Dte date={this.date} format={'l'} />
    ) : undefined
  }

  public get dateClearedString(): React.ReactElement | undefined {
    return this.clearedAt !== undefined ? (
      <Dte date={this.clearedAt} format={'l'} />
    ) : undefined
  }

  public get isPending(): boolean {
    return this.status === TransactionStatus.Pending
  }

  public get isCleared(): boolean {
    return this.status === TransactionStatus.Cleared
  }

  public get isDeclined(): boolean {
    return this.status === TransactionStatus.Declined
  }

  public get isReversal(): boolean {
    return this.status === TransactionStatus.AuthReversal
  }

  public get isCredit(): boolean {
    return this.type === TransactionType.Credit && !this.isPending
  }

  public get isNonCreditCleared(): boolean {
    return this.status === TransactionStatus.Cleared && !this.isCredit
  }

  public get isPhysical(): boolean {
    return this.source === TransactionSource.Physical
  }

  public get isVirtual(): boolean {
    return this.source === TransactionSource.Virtual
  }

  public get isOther(): boolean {
    return this.source === TransactionSource.Other
  }

  public notPushedToConnectedPlatform = (
    activePlatform?: OrganizationConnectedPlatform,
  ): boolean => {
    return (
      this.connectedPlatforms === undefined ||
      this.connectedPlatforms.filter(
        (connectedPlatform) =>
          connectedPlatform.platform === activePlatform &&
          (connectedPlatform.syncStatus === ConnectedPlatformStatus.Success ||
            connectedPlatform.syncStatus === ConnectedPlatformStatus.Pending),
      ).length === 0
    )
  }

  public canPushToConnectedPlatform = (
    activePlatform: OrganizationConnectedPlatform | undefined,
    mandatoryExpenseDetails: ExpenseCategoryResponse[] | undefined,
  ): boolean => {
    if (this.isSplit) {
      const details = this.expenseDetails ?? []
      const anyMissingAccount =
        new Set(
          details.filter((d) => d.splitIndex != null).map((d) => d.splitIndex),
        ).size !==
        new Set(
          details
            ?.filter((d) => d.categoryType === 'ACCOUNT')
            .map((d) => d.splitIndex),
        ).size
      return (
        this.isCleared &&
        this.notPushedToConnectedPlatform(activePlatform) &&
        // Only check for missing Account field
        !anyMissingAccount
      )
    }

    return (
      this.isCleared &&
      this.notPushedToConnectedPlatform(activePlatform) &&
      // Only check for missing Account field
      this.expenseDetails?.find(
        (txnEC) => txnEC.categoryType === ExpenseCategoryType.Account,
      )?.labelName !== undefined
    )
  }

  public checkMissingRequiredExpenseCategory = (
    activePlatform: OrganizationConnectedPlatform | undefined,
    mandatoryExpenseDetails: ExpenseCategoryResponse[] | undefined,
  ): boolean => {
    if (activePlatform !== undefined) {
      // we only care about missing for un-synced pending and cleared transactions
      if (
        (!this.isCleared && !this.isPending) ||
        !this.notPushedToConnectedPlatform(activePlatform)
      )
        return false
      const missingExpenseCategories =
        mandatoryExpenseDetails?.filter((expCat) => {
          const isCustomerOrVendor =
            expCat?.type === ExpenseCategoryType.Customer ||
            expCat?.type === ExpenseCategoryType.Vendor
          if (isCustomerOrVendor) {
            return (
              this.expenseDetails?.find((txnEC) => {
                return txnEC.categoryType === expCat.type
              }) === undefined
            )
          } else {
            return (
              this.expenseDetails?.find((txnEC) => {
                return txnEC.categoryId === expCat.id
              }) === undefined
            )
          }
        }) ?? []
      return missingExpenseCategories.length > 0
    } else {
      const missingExpenseCategories =
        mandatoryExpenseDetails?.filter(
          (expCat) =>
            this.expenseDetails?.find(
              (txnEC) => txnEC.categoryId === expCat.id,
            ) === undefined,
        ) ?? []
      return missingExpenseCategories.length > 0
    }
  }

  public checkSingleMissingRequiredExpenseCategory = (
    expenseCategory: ExpenseCategoryResponse,
    activePlatform: OrganizationConnectedPlatform | undefined,
  ): boolean => {
    if (!expenseCategory.requiredForTransactions) return false

    if (activePlatform !== undefined) {
      // we only care about missing for un-synced pending and cleared transactions
      if (
        (!this.isCleared && !this.isPending) ||
        !this.notPushedToConnectedPlatform(activePlatform)
      )
        return false
      const isCustomerOrVendor =
        expenseCategory?.type === ExpenseCategoryType.Customer ||
        expenseCategory?.type === ExpenseCategoryType.Vendor
      if (isCustomerOrVendor) {
        return (
          this.expenseDetails?.find((txnEC) => {
            return txnEC.categoryType === expenseCategory.type
          }) === undefined
        )
      } else {
        return (
          this.expenseDetails?.find((txnEC) => {
            return txnEC.categoryId === expenseCategory.id
          }) === undefined
        )
      }
    } else {
      return (
        this.expenseDetails?.find(
          (txnEC) => txnEC.categoryId === expenseCategory.id,
        ) === undefined
      )
    }
  }

  public get pseudoStatusModifiers(): Record<string, boolean> {
    return {
      isPending: this.isPending,
      isNonCreditCleared: this.isNonCreditCleared,
      isDeclined: this.isDeclined,
      isCredit: this.isCredit,
      isReversal: this.isReversal,
    }
  }

  public get clearingOrAuthAmountCents(): number | undefined {
    switch (this.status) {
      case TransactionStatus.Declined:
      case TransactionStatus.Pending:
      case TransactionStatus.AuthReversal: {
        return this.authBillingAmountCents
      }
      default: {
        return this.clearingBillingAmountCents
      }
    }
  }

  public get clearingOrAuthAmount(): number | undefined {
    return this.clearingOrAuthAmountCents !== undefined
      ? toDollars(this.clearingOrAuthAmountCents)
      : undefined
  }

  public get currency(): ExtendCurrency | undefined {
    return this.clearingBillingAmountCents !== undefined &&
      this.clearingBillingCurrency !== undefined
      ? this.clearingBillingCurrency
      : this.authBillingAmountCents !== undefined &&
          this.authBillingCurrency !== undefined
        ? this.authBillingCurrency
        : undefined
  }

  public clearingOrAuthLabel(hideCurrency?: boolean): React.ReactElement {
    const currency = this.currency || defaultCurrency

    return this.clearingOrAuthAmount !== undefined ? (
      this.isCredit ? (
        translateNode('- ${amount}', {
          amount: (
            <Crncy
              currency={currency}
              amount={this.clearingOrAuthAmount}
              hideCurrency={hideCurrency}
            />
          ),
        })
      ) : (
        <Crncy
          currency={currency}
          amount={this.clearingOrAuthAmount}
          hideCurrency={hideCurrency}
        />
      )
    ) : (
      <EmptyLabel />
    )
  }

  public clearingOrAuthDateMoment(
    isClearedAtSelected?: boolean,
  ): Moment | undefined {
    if (isClearedAtSelected) {
      return this.clearedAt ? dateFormatMoment(this.clearedAt) : undefined
    } else {
      return this.date ? dateFormatMoment(this.date) : undefined
    }
  }

  public get pseudoStatusLabel(): React.ReactElement {
    if (this.isCredit) {
      if (this.isReversal) {
        return (
          <div>
            <div
              css={css`
                opacity: 0.5;
                display: inline-block;
              `}
            >
              {translateNode('Reversal')}
            </div>
            <Tooltip
              css={css`
                height: auto;
                display: inline-block;
                vertical-align: middle;
              `}
              openLeft={true}
            >
              <p>
                {translateNode(
                  'A reversal occurs when a merchant has fully or partially cancelled an authorized charge on the same day as when it was made.',
                )}
              </p>
            </Tooltip>
          </div>
        )
      }
      return translateNode('Credit')
    } else if (this.isNonCreditCleared) {
      return translateNode('Cleared')
    } else if (this.isDeclined) {
      return translateNode('Declined')
    } else if (this.isPending) {
      return translateNode('Pending')
    } else if (this.isReversal) {
      return translateNode('Reversal')
    } else {
      return <EmptyLabel />
    }
  }

  public get pseudoStatusLabelNew(): React.ReactElement {
    if (this.isReversal) {
      return (
        <div className={'flex-row gap-1 align-center'}>
          {translateNode('Reversal')}
          <TooltipIcon placement={'top'}>
            {translateNode(
              'A reversal occurs when a merchant has fully or partially cancelled an authorized charge on the same day as when it was made.',
            )}
          </TooltipIcon>
        </div>
      )
    } else if (this.isCredit) {
      return translateNode('Credit')
    } else if (this.isNonCreditCleared) {
      return translateNode('Cleared')
    } else if (this.isDeclined) {
      return translateNode('Declined')
    } else if (this.isPending) {
      return translateNode('Pending')
    } else {
      return <EmptyLabel />
    }
  }

  public get pseudoStatusLabelRowView(): React.ReactElement {
    if (this.isPending) {
      return translateNode('Pending')
    } else if (this.isCredit) {
      if (this.isReversal) {
        return translateNode('Reversal')
      }
      return translateNode('Credit')
    } else if (this.isNonCreditCleared) {
      return translateNode('Cleared')
    } else if (this.isDeclined) {
      return translateNode('Declined')
    } else if (this.isReversal) {
      return translateNode('Reversal')
    } else {
      return <EmptyLabel />
    }
  }

  public get sourceLabel(): React.ReactElement {
    switch (this.source) {
      case TransactionSource.Other: {
        return translateNode('Other')
      }
      case TransactionSource.Physical: {
        return translateNode('Physical')
      }
      case TransactionSource.Virtual: {
        return translateNode('Virtual')
      }
    }
  }
}

export interface TransactionDetailResponse extends TransactionListResponse {
  receiptAttachments: ReceiptAttachmentResponse[]
  receiptRecoveryCase?: ReceiptRecoveryCase
}

export enum ReceiptRecoveryLifecycleState {
  WaitingOnSpender = 'WAITING_ON_SPENDER',
  MissingReceipt = 'MISSING_RECEIPT',
  NeedsReview = 'NEEDS_REVIEW',
  ReceiptReconciled = 'RECEIPT_RECONCILED',
}

/** Parsed receipt data the agent recovered alongside the attachment. */
export interface RecoveredReceiptEvidence {
  amountCents?: number
  merchantName?: string
  date?: string
  submittedAt?: string
  submittedByDisplayName?: string
}

export interface ReceiptRecoveryCase {
  /** Display state retained for the local preview and mapped live API cases. */
  lifecycleState?: ReceiptRecoveryLifecycleState
  /** Live Receipt Agent case fields returned by transaction detail. */
  id?: string
  status?: string
  statusUpdatedAt?: string
  version?: number
  receiptAttachmentId?: string
  lastAgentRunAt?: string
  recoveredAttachmentId?: string
  recoveredFileName?: string
  recoveredFrom?: 'GMAIL' | 'GOOGLE_DRIVE'
  recoveredReceipt?: RecoveredReceiptEvidence
  reconciledByDisplayName?: string
  reconciledAt?: string
}

export interface ReceiptRecoveryDisplayCase extends ReceiptRecoveryCase {
  lifecycleState: ReceiptRecoveryLifecycleState
}

export class TransactionDetailModel extends TransactionListModel {
  public receiptAttachments: ReceiptAttachmentResponse[]
  public receiptRecoveryCase?: ReceiptRecoveryCase

  public constructor(data: TransactionDetailResponse) {
    super(data)
    this.receiptAttachments = data.receiptAttachments.map(
      (att) => new ReceiptAttachmentModel(att),
    )
    this.receiptRecoveryCase = data.receiptRecoveryCase
  }
}

export interface ExpenseReviewResponse {
  id: string
  transactionId: string
  finalApprovalStatus: TransactionFinalApprovalStatus
  managerReviewStatus: TransactionManagerReviewStatus
  managerRejectionReason?: TransactionManagerRejectionReason
  managerRejectionNote?: string
  managerReviewId?: string
  managerId: string
}
